<script lang="ts" setup>
/**
 * Documentation of a11y:
 *
 * Pagination here doesn't behave as links, but as buttons.
 * So it's a weird mix of semantically being a pagination but also a kind of tablist/carousel.
 *
 * So we use the pagination approach because it makes sense semantically.
 * but since it's buttons controlling another "panel"/"view" we have to reference this
 * in the buttons' aria-controls attribute.
 *
 * Set the id attribute of the aria-controls element to the same value as the aria-controls attribute of the button.
 * Also set the aria-describedby attribute to the statusId. Note that aria-controls should be able to "focus" the controlled
 * element, but currently on VoiceOver it doesn't. It has to be a link for that to work, but it is semantically buttons.
 *
 * For more information see:
 * - https://designsystem.digital.gov/components/pagination/
 * - https://design-system.w3.org/components/pagination.html
 * - https://www.a11ymatters.com/pattern/pagination/
 * - https://github.com/uswds/uswds/blob/48bfdf2960d7cbd043dac5bf71a9616ec1defa65/packages/usa-pagination/src/usa-pagination.twig
 * - https://datatables.net/ (javascript controlled content)
 */

import LeChevronLeft from "@dsk/icons/LeChevronLeft.vue";
import LeChevronRight from "@dsk/icons/LeChevronRight.vue";
import { generateRandomId } from "@dsk/utils";
import { computed, nextTick } from "vue";
import { useI18n } from "vue-i18n";

import LePaginateArrowButton from "./LePaginateArrowButton.vue";
import LePaginateButton from "./LePaginateButton.vue";
import LePaginateNumberingItem from "./LePaginateNumberingItem.vue";

const props = defineProps<{
  controls: string;
  label?: string;
  currentPage: number;
  totalPages: number;
}>();

const emit = defineEmits<{
  (event: "update:currentPage", page: number): void;
}>();

const statusId = `le-paginate__status--${generateRandomId()}`;
defineExpose({
  statusId // Allow consumer to reference the ID in e.g. aria-describedby
});

const { t } = useI18n();

interface NumberingItem {
  type: "page" | "ellipsis";
  index: number;
}

interface Page extends NumberingItem {
  type: "page";
  index: number;
  pageNumber: number;
}

interface Ellipsis extends NumberingItem {
  type: "ellipsis";
  index: number;
  text: string;
}

const isPage = (item: NumberingItem): item is Page => item.type === "page";
const isEllipsis = (item: NumberingItem): item is Ellipsis => item.type === "ellipsis";

const pages = computed<Page[]>(() =>
  Array.from({ length: props.totalPages }, (_, i) => ({ type: "page", index: i, pageNumber: i + 1 }))
);

const makeEllipsis = (index: number): Ellipsis => ({ type: "ellipsis", text: "…", index });

const numberingItems = computed<NumberingItem[]>(() => {
  // List all items if less than 8 pages
  // Always show adjacent pages to the current page
  // Ellipsis is where the gap is to large to display (we can max display 7 items)
  // See https://designsystem.digital.gov/components/pagination/#behaviors
  if (props.totalPages < 8) {
    return pages.value;
  }

  const currentPageIndex = props.currentPage;
  const lastPageIndex = props.totalPages - 1;

  // User is at the start of a long dataset
  // Example: 1, 2, 3, *4*, 5 … 8 #}
  // Doesn't apply when user gets to 5 of 8
  if (currentPageIndex < 4) {
    const items: NumberingItem[] = pages.value.slice(0, 5);

    items.push(makeEllipsis(items.length));
    items.push(pages.value[lastPageIndex]);
    return items;
  }

  // When user is close to the end of dataset
  // Example: 1 … 4, *5*, 6, 7, 8
  // Doesn't apply when user gets to 4 of 8
  if (currentPageIndex >= props.totalPages - 4) {
    const items: NumberingItem[] = pages.value.slice(0, 1);

    items.push(makeEllipsis(items.length));
    items.push(...pages.value.slice(lastPageIndex - 4));
    return items;
  }

  // Default case: Current - 1, Current, Current + 1
  // Example: 1 … 21, *22*, 23 … 50
  // Example: 1 … 4, *5*, 6 … 9
  const items: NumberingItem[] = pages.value.slice(0, 1);

  items.push(makeEllipsis(items.length));
  items.push(...pages.value.slice(currentPageIndex - 1, currentPageIndex + 2));
  items.push(makeEllipsis(items.length));
  items.push(pages.value[lastPageIndex]);

  return items;
});

const isFirstPage = computed(() => props.currentPage === 0);
const isLastPage = computed(() => props.currentPage === props.totalPages - 1);

const currentPageNumber = computed(() => props.currentPage + 1);

const onPageNumberClick = async (item: Page) => {
  emit("update:currentPage", item.index);

  // Prevent focus being N +/- 1 when switching using the page numbers
  await nextTick();
  document.getElementById(`le-paginate-pg-${item.pageNumber}`)?.focus();
};
</script>

<template>
  <nav role="navigation" :aria-label="label === undefined ? t('navigation-label') : label" class="le-paginate">
    <ul class="le-paginate__list">
      <li class="le-paginate__list-item le-paginate__list-item--arrow-left">
        <LePaginateArrowButton
          :controls="controls"
          :label="t('prev-page')"
          :active="false"
          :disabled="isFirstPage"
          @click="emit('update:currentPage', currentPage - 1)"
        >
          <LeChevronLeft role="presentation" />
        </LePaginateArrowButton>
      </li>
      <li v-for="(item, key) in numberingItems" :key="key">
        <template v-if="isPage(item)">
          <LePaginateButton
            :id="`le-paginate-pg-${item.pageNumber}`"
            :controls="controls"
            :label="
              currentPage === item.index
                ? t('selected-page', { page: item.pageNumber })
                : t('go-to-page', { page: item.pageNumber })
            "
            :active="currentPage === item.index"
            @click="onPageNumberClick(item)"
          >
            {{ item.pageNumber }}
          </LePaginateButton>
        </template>
        <template v-else-if="isEllipsis(item)">
          <LePaginateNumberingItem tag="span" class="le-paginate__ellipsis">{{ item.text }}</LePaginateNumberingItem>
        </template>
      </li>
      <li class="le-paginate__list-item le-paginate__list-item--arrow-right">
        <LePaginateArrowButton
          :controls="controls"
          :label="t('next-page')"
          :active="false"
          :disabled="isLastPage"
          @click="emit('update:currentPage', currentPage + 1)"
        >
          <LeChevronRight role="presentation" />
        </LePaginateArrowButton>
      </li>
    </ul>
    <div :id="statusId" class="sr-only" role="status" aria-live="polite">
      {{ t("pagination-status", { current: currentPageNumber, total: totalPages }) }}
    </div>
  </nav>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";

.le-paginate {
  &__list {
    display: flex;
    gap: 6px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__list-item {
    &--arrow-left {
      padding-right: 24px;
    }

    &--arrow-right {
      padding-left: 24px;
    }
  }
}
</style>

<i18n lang="json">
{
  "da": {
    "navigation-label": "Navigation af sidenummerering",
    "prev-page": "Forrige side",
    "next-page": "Næste side",
    "go-to-page": "Gå til side {page}",
    "selected-page": "Nuværende side, {page}",
    "pagination-status": "Side {current} af {total}"
  },
  "en": {
    "navigation-label": "Pagination navigation",
    "prev-page": "Previous page",
    "next-page": "Next page",
    "go-to-page": "Go to page {page}",
    "selected-page": "Current page, {page}",
    "pagination-status": "Page {current} of {total}"
  },
  "de": {
    "navigation-label": "Seitennavigation",
    "prev-page": "Vorherige Seite",
    "next-page": "Nächste Seite",
    "go-to-page": "Gehe zu Seite {page}",
    "selected-page": "Aktuelle Seite, {page}",
    "pagination-status": "Seite {current} von {total}"
  }
}
</i18n>
